/**
 * ===============================================
 * ARKUNO Founder Profile Animations
 * プロフィールページ専用アニメーションスクリプト
 * ===============================================
 */

(function() {
  'use strict';

  class FounderProfileAnimations {
    constructor() {
      this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.isReducedMotion) return;

      this.init();
    }

    init() {
      // ページロード時のエントランスアニメーション
      this.initEntranceAnimations();
      
      // スクロールトリガーアニメーション
      this.initScrollAnimations();
      
      // パララックス効果
      this.initParallaxEffect();
      
      // カードホバーアニメーション
      this.initCardAnimations();
      
      // テキストアニメーション
      this.initTextAnimations();
      
      // スクロール進行インジケーター
      this.initScrollProgress();
      
      // カーソルグローエフェクト（デスクトップのみ）
      if (window.innerWidth >= 750) {
        this.initCursorGlow();
      }
      
      // 背景グラデーションアニメーション
      this.initBackgroundAnimation();
      
      // クリックリップルエフェクト
      this.initRippleEffect();
      
      // 背景パーティクル（控えめ）
      if (window.innerWidth >= 750) {
        this.initBackgroundParticles();
      }
      
      // ビジュアルセパレーターのドットインタラクション
      this.initVisualSeparatorInteraction();
      
      // ストーリーカードのアコーディオン機能
      this.initStoryCardAccordion();
    }

    /**
     * ページロード時のエントランスアニメーション
     */
    initEntranceAnimations() {
      // ヒーロー画像のフェードイン＆ズームアウト
      const heroImage = document.querySelector('.founder-hero__image-wrapper');
      if (heroImage) {
        heroImage.classList.add('ark-animate-hero');
      }

      // タイトルの段階的フェードイン
      const title = document.querySelector('.founder-title');
      const subtitle = document.querySelector('.founder-subtitle');
      const tagline = document.querySelector('.founder-tagline');
      const description = document.querySelector('.founder-description');

      if (title) {
        setTimeout(() => title.classList.add('ark-animate-fade-up'), 200);
      }
      if (subtitle) {
        setTimeout(() => subtitle.classList.add('ark-animate-fade-up'), 400);
      }
      if (tagline) {
        setTimeout(() => tagline.classList.add('ark-animate-fade-up'), 600);
      }
      if (description) {
        setTimeout(() => description.classList.add('ark-animate-fade-up'), 800);
      }
    }

    /**
     * スクロールトリガーアニメーション
     */
    initScrollAnimations() {
      const observerOptions = {
        root: null,
        rootMargin: '0px 0px -100px 0px',
        threshold: 0.1
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            // 要素が表示されたらアニメーションを適用
            setTimeout(() => {
              entry.target.classList.add('ark-in-view');
            }, index * 100); // 段階的に表示
            
            // 一度表示されたら監視を解除
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      // アニメーション対象要素を監視
      const animateElements = document.querySelectorAll(
        '.founder-story-card, .founder-section, .founder-image-text, .founder-philosophy, .founder-mission, .founder-visual-separator'
      );

      animateElements.forEach(el => {
        el.classList.add('ark-scroll-animate');
        observer.observe(el);
      });

      // ギャラリーアイテムの段階的表示
      const galleryItems = document.querySelectorAll('.founder-gallery__item');
      if (galleryItems.length > 0) {
        const galleryObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const items = Array.from(galleryItems);
              items.forEach((item, index) => {
                setTimeout(() => {
                  item.classList.add('ark-in-view');
                }, index * 80);
              });
              galleryObserver.disconnect();
            }
          });
        }, observerOptions);

        galleryObserver.observe(galleryItems[0].parentElement);
      }
    }

    /**
     * パララックス効果（控えめ）
     */
    initParallaxEffect() {
      const heroImage = document.querySelector('.founder-hero__image-wrapper img');
      if (!heroImage) return;

      let ticking = false;
      
      const updateParallax = () => {
        const scrolled = window.pageYOffset;
        const heroSection = document.querySelector('.founder-hero');
        
        if (heroSection) {
          const heroBottom = heroSection.offsetTop + heroSection.offsetHeight;
          
          if (scrolled < heroBottom) {
            const yPos = scrolled * 0.3; // パララックス速度（控えめ）
            heroImage.style.transform = `translate3d(0, ${yPos}px, 0) scale(1.1)`;
          }
        }
        
        ticking = false;
      };

      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(updateParallax);
          ticking = true;
        }
      }, { passive: true });
    }

    /**
     * カードアニメーション
     */
    initCardAnimations() {
      // カード画像のホバー時ズーム効果
      const cardImages = document.querySelectorAll('.founder-story-card__image img');
      cardImages.forEach(img => {
        const parent = img.closest('.founder-story-card__image');
        if (parent) {
          parent.addEventListener('mouseenter', () => {
            img.style.transform = 'scale(1.05)';
          });
          parent.addEventListener('mouseleave', () => {
            img.style.transform = 'scale(1)';
          });
        }
      });
    }

    /**
     * テキストアニメーション（文字の段階的表示）
     */
    initTextAnimations() {
      const tagline = document.querySelector('.founder-tagline');
      if (!tagline) return;

      // タグラインの文字を一文字ずつspan要素で囲む
      const text = tagline.textContent;
      tagline.innerHTML = '';
      
      Array.from(text).forEach((char, index) => {
        const span = document.createElement('span');
        span.textContent = char;
        span.classList.add('ark-char');
        span.style.animationDelay = `${index * 0.03}s`;
        tagline.appendChild(span);
      });
    }

    /**
     * スクロール進行インジケーター
     */
    initScrollProgress() {
      // スクロール進行バーを作成
      const progressBar = document.createElement('div');
      progressBar.className = 'ark-scroll-progress';
      progressBar.innerHTML = '<div class="ark-scroll-progress__bar"></div>';
      document.body.appendChild(progressBar);

      const bar = progressBar.querySelector('.ark-scroll-progress__bar');
      
      let ticking = false;
      const updateProgress = () => {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight - windowHeight;
        const scrolled = window.pageYOffset;
        const progress = (scrolled / documentHeight) * 100;
        
        bar.style.width = `${Math.min(progress, 100)}%`;
        ticking = false;
      };

      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(updateProgress);
          ticking = true;
        }
      }, { passive: true });

      // 初期値設定
      updateProgress();
    }

    /**
     * カーソルグローエフェクト
     */
    initCursorGlow() {
      const glow = document.createElement('div');
      glow.className = 'ark-cursor-glow';
      document.body.appendChild(glow);

      let mouseX = 0;
      let mouseY = 0;
      let glowX = 0;
      let glowY = 0;

      document.addEventListener('mousemove', (e) => {
        mouseX = e.clientX;
        mouseY = e.clientY;
      });

      // スムーズなグローの移動
      const animateGlow = () => {
        glowX += (mouseX - glowX) * 0.1;
        glowY += (mouseY - glowY) * 0.1;
        
        glow.style.transform = `translate(${glowX}px, ${glowY}px)`;
        requestAnimationFrame(animateGlow);
      };

      animateGlow();

      // カード上でグローを強調
      const cards = document.querySelectorAll('.founder-story-card, .founder-gallery__item');
      cards.forEach(card => {
        card.addEventListener('mouseenter', () => {
          glow.classList.add('ark-cursor-glow--active');
        });
        card.addEventListener('mouseleave', () => {
          glow.classList.remove('ark-cursor-glow--active');
        });
      });
    }

    /**
     * 背景グラデーションアニメーション
     */
    initBackgroundAnimation() {
      const founderProfile = document.querySelector('.founder-profile');
      if (!founderProfile) return;

      // 微細なグラデーション背景を追加
      const gradientOverlay = document.createElement('div');
      gradientOverlay.className = 'ark-gradient-overlay';
      founderProfile.insertBefore(gradientOverlay, founderProfile.firstChild);

      // スクロールに応じてグラデーションを変化
      let ticking = false;
      const updateGradient = () => {
        const scrolled = window.pageYOffset;
        const opacity = Math.min(scrolled / 1000, 0.03); // 最大3%の不透明度
        gradientOverlay.style.opacity = opacity;
        ticking = false;
      };

      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(updateGradient);
          ticking = true;
        }
      }, { passive: true });
    }

    /**
     * クリックリップルエフェクト
     */
    initRippleEffect() {
      const clickableElements = document.querySelectorAll(
        '.founder-story-card, .founder-gallery__item, .founder-social__link'
      );

      clickableElements.forEach(element => {
        element.addEventListener('click', function(e) {
          // リップル要素を作成
          const ripple = document.createElement('span');
          ripple.className = 'ark-ripple';
          
          // クリック位置を計算
          const rect = this.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          
          ripple.style.left = `${x}px`;
          ripple.style.top = `${y}px`;
          
          this.appendChild(ripple);
          
          // アニメーション終了後に削除
          setTimeout(() => {
            ripple.remove();
          }, 600);
        });
      });
    }

    /**
     * 背景パーティクル（控えめ）
     */
    initBackgroundParticles() {
      const canvas = document.createElement('canvas');
      canvas.className = 'ark-particles-canvas';
      canvas.style.position = 'fixed';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '0';
      canvas.style.opacity = '0.4'; // 完全に表示
      
      document.body.insertBefore(canvas, document.body.firstChild);

      const ctx = canvas.getContext('2d');
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      // パーティクルの配列
      const particles = [];
      const particleCount = 25; // 控えめなパーティクル数

      class Particle {
        constructor() {
          this.reset();
        }

        reset() {
          this.x = Math.random() * canvas.width;
          this.y = Math.random() * canvas.height;
          this.size = Math.random() * 4 + 2; // より大きく
          this.speedX = Math.random() * 0.8 - 0.4;
          this.speedY = Math.random() * 0.8 - 0.4;
          this.opacity = Math.random() * 0.6 + 0.3; // より濃く
          this.pulseSpeed = Math.random() * 0.015 + 0.008;
          this.pulsePhase = Math.random() * Math.PI * 2;
          
          // 5つの点を象徴する5色のパーティクル（より濃い色に）
          const colorType = Math.random();
          if (colorType < 0.2) {
            // エンジニア：ブルー（テクノロジー）
            this.color = { r: 100, g: 150, b: 200 };
          } else if (colorType < 0.4) {
            // カメラ：ピンク（クリエイティブ）
            this.color = { r: 200, g: 120, b: 150 };
          } else if (colorType < 0.6) {
            // レーザークラフト：オレンジ（情熱、レーザー）
            this.color = { r: 220, g: 140, b: 80 };
          } else if (colorType < 0.8) {
            // レジンアート：ターコイズ（沖縄の海）
            this.color = { r: 80, g: 180, b: 180 };
          } else {
            // 人・チームづくり：グリーン（成長）
            this.color = { r: 120, g: 180, b: 120 };
          }
        }

        update() {
          this.x += this.speedX;
          this.y += this.speedY;
          
          // パルス効果
          this.pulsePhase += this.pulseSpeed;

          // 画面外に出たらリセット
          if (this.x < 0 || this.x > canvas.width || this.y < 0 || this.y > canvas.height) {
            this.reset();
          }
        }

        draw() {
          const pulse = Math.sin(this.pulsePhase) * 0.3 + 0.7;
          const currentOpacity = this.opacity * pulse;
          const currentSize = this.size * pulse;
          
          // 美しいグラデーションで光っているように見せる
          const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, currentSize * 2.5);
          gradient.addColorStop(0, `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${currentOpacity})`);
          gradient.addColorStop(0.5, `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${currentOpacity * 0.5})`);
          gradient.addColorStop(1, `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, 0)`);
          
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(this.x, this.y, currentSize * 2.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // パーティクルを生成
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }

      // アニメーションループ
      const animate = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        particles.forEach(particle => {
          particle.update();
          particle.draw();
        });

        requestAnimationFrame(animate);
      };

      animate();

      // ウィンドウリサイズ対応
      window.addEventListener('resize', () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      });
    }

    /**
     * ビジュアルセパレーターのドットインタラクション
     */
    initVisualSeparatorInteraction() {
      const dots = document.querySelectorAll('.founder-visual-separator__dot');
      const storyCards = document.querySelectorAll('.founder-story-card');
      
      if (dots.length === 0 || storyCards.length === 0) return;

      // ビジュアルセパレーターのドットは表示のみ（スクロール機能は無効化）
      // ストーリーカードのドットで直接開閉する方式に統一

      // スクロール時にアクティブなドットをハイライト
      let ticking = false;
      const updateActiveDot = () => {
        const scrollPosition = window.pageYOffset + window.innerHeight / 2;
        
        storyCards.forEach((card, index) => {
          const cardTop = card.offsetTop;
          const cardBottom = cardTop + card.offsetHeight;
          
          if (scrollPosition >= cardTop && scrollPosition <= cardBottom) {
            // アクティブなドットを強調
            dots.forEach(d => d.classList.remove('active'));
            if (dots[index]) {
              dots[index].classList.add('active');
            }
          }
        });
        
        ticking = false;
      };

      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(updateActiveDot);
          ticking = true;
        }
      }, { passive: true });
    }

    /**
     * ストーリーカードのアコーディオン機能
     */
    initStoryCardAccordion() {
      const cards = document.querySelectorAll('.founder-story-card--collapsible');
      
      if (cards.length === 0) return;

      cards.forEach((card, index) => {
        const icon = card.querySelector('.founder-story-card__icon');
        const header = card.querySelector('.founder-story-card__header');
        const body = card.querySelector('.founder-story-card__body');
        
        if (!icon || !header || !body) return;

        // クリックイベント
        const toggleCard = (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          const isOpen = card.classList.contains('founder-story-card--open');
          
          if (isOpen) {
            // 閉じる
            card.classList.remove('founder-story-card--open');
            icon.setAttribute('aria-expanded', 'false');
            body.setAttribute('aria-hidden', 'true');
          } else {
            // 他のカードをすべて閉じる
            cards.forEach((otherCard) => {
              if (otherCard !== card) {
                const otherIcon = otherCard.querySelector('.founder-story-card__icon');
                const otherBody = otherCard.querySelector('.founder-story-card__body');
                
                otherCard.classList.remove('founder-story-card--open');
                if (otherIcon) otherIcon.setAttribute('aria-expanded', 'false');
                if (otherBody) otherBody.setAttribute('aria-hidden', 'true');
              }
            });
            
            // このカードを開く
            card.classList.add('founder-story-card--open');
            icon.setAttribute('aria-expanded', 'true');
            body.setAttribute('aria-hidden', 'false');
            
            // スクロールは無効化（その場で開閉）
          }
        };

        // アイコン（ドット）にクリックイベントを追加
        icon.addEventListener('click', toggleCard);

        // アクセシビリティ属性
        icon.setAttribute('role', 'button');
        icon.setAttribute('aria-expanded', 'false');
        icon.setAttribute('tabindex', '0');
        icon.setAttribute('aria-label', 'ストーリーを開く・閉じる');
        body.setAttribute('aria-hidden', 'true');

        // キーボード操作（Enter/Space）
        icon.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleCard(e);
          }
        });

        // 最初のカードをデフォルトで開く（オプション）
        if (index === 0) {
          card.classList.add('founder-story-card--open');
          icon.setAttribute('aria-expanded', 'true');
          body.setAttribute('aria-hidden', 'false');
        }
      });
    }
  }

  /**
   * スムーススクロール（アンカーリンク用）
   */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#') return;
        
        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  /**
   * 初期化
   */
  function init() {
    // プロフィールページかどうかを確認
    const isFounderProfile = document.querySelector('.founder-profile');
    if (!isFounderProfile) return;

    // アニメーションを初期化
    new FounderProfileAnimations();
    
    // スムーススクロール
    initSmoothScroll();

    // ページ読み込み完了時に全体をフェードイン
    window.addEventListener('load', () => {
      document.body.classList.add('ark-page-loaded');
    });
  }

  // DOM読み込み完了後に初期化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify テーマエディタ対応
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }

})();

